import { getStoredToken } from './auth'
import { getFileNameFromContentDisposition } from '@/utils/contentDisposition'

const API_BASE_URL = import.meta.env.VITE_DAPI_URL || ''

export class ApiRequestError extends Error {
  status: number
  details: string[]
  debug?: string

  constructor(message: string, status: number, details: string[] = [], debug?: string) {
    super(message)
    this.name = 'ApiRequestError'
    this.status = status
    this.details = details
    this.debug = debug
  }
}

const getAuthorizationHeader = () => {
  const token = getStoredToken()

  if (!token || !('jwt' in token) || !token.jwt) {
    throw new ApiRequestError('Missing authentication token.', 401)
  }

  return { authorization: `bearer ${token.jwt}` }
}

const handleFailedResponse = async (response: Response) => {
  let message = 'Request failed'
  let details: string[] = []
  let debug: string | undefined
  try {
    const data = (await response.json()) as {
      message?: string
      error?: { message?: string; errors?: string[]; details?: unknown; route?: string }
      errors?: string[]
      errorDetails?: string[]
      details?: unknown
    }

    if (data?.message) {
      message = data.message
    }

    if (data?.error?.message) {
      message = `${message}: ${data.error.message}`
    }

    if (Array.isArray(data?.errors)) {
      details = details.concat(data.errors)
    }

    if (Array.isArray(data?.error?.errors)) {
      details = details.concat(data.error.errors)
    }

    if (Array.isArray(data?.errorDetails)) {
      details = details.concat(data.errorDetails)
    }

    if (data?.details) {
      debug = JSON.stringify(data.details, null, 2)
    } else if (data?.error?.details) {
      debug = JSON.stringify(data.error.details, null, 2)
    } else {
      debug = JSON.stringify(data, null, 2)
    }
  } catch {
    // Keep fallback message.
  }

  throw new ApiRequestError(message, response.status, details, debug)
}

const requestUrl = (path: string) => {
  if (!API_BASE_URL) {
    throw new Error('Missing API base URL (VITE_DAPI_URL).')
  }

  return `${API_BASE_URL}${path}`
}

export const requestJson = async <T>(path: string, method: 'GET' | 'POST', body?: unknown): Promise<T> => {
  const response = await fetch(requestUrl(path), {
    method,
    credentials: 'include',
    headers: {
      'content-type': 'application/json',
      ...getAuthorizationHeader(),
    },
    body: body ? JSON.stringify(body) : undefined,
  })

  if (!response.ok) {
    await handleFailedResponse(response)
  }

  const text = await response.text()
  if (!text) {
    throw new Error(`Expected JSON response from ${path} but got an empty body`)
  }

  return JSON.parse(text) as T
}

export const requestVoid = async (path: string, method: 'GET' | 'POST' | 'DELETE', body?: unknown): Promise<void> => {
  const response = await fetch(requestUrl(path), {
    method,
    credentials: 'include',
    headers: {
      'content-type': 'application/json',
      ...getAuthorizationHeader(),
    },
    body: body ? JSON.stringify(body) : undefined,
  })

  if (!response.ok) {
    await handleFailedResponse(response)
  }
}

export const multipartRequest = async <T>(path: string, method: 'POST', body: FormData): Promise<T> => {
  const response = await fetch(requestUrl(path), {
    method,
    credentials: 'include',
    headers: getAuthorizationHeader(),
    body,
  })

  if (!response.ok) {
    await handleFailedResponse(response)
  }

  return (await response.json()) as T
}

export const blobRequest = async (path: string): Promise<{ blob: Blob; contentType: string; fileName?: string }> => {
  const response = await fetch(requestUrl(path), {
    method: 'GET',
    credentials: 'include',
    headers: getAuthorizationHeader(),
  })

  if (!response.ok) {
    await handleFailedResponse(response)
  }

  const blob = await response.blob()
  const contentType = response.headers.get('content-type') ?? blob.type
  const contentDisposition = response.headers.get('content-disposition') ?? undefined

  return {
    blob,
    contentType,
    fileName: getFileNameFromContentDisposition(contentDisposition),
  }
}
